import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';

// Pages
import Home from './pages/index';
import About from './pages/about';
import Contact from './pages/contact';
import Services from './pages/services';
import Projects from './pages/projects';
import Donations from './pages/donations';
import Team from './pages/team';
import Blog from './pages/blog';
import Blog2 from './pages/blog-2';
import BlogDetails from './pages/blog-details';
import EventDetails from './pages/event-details';
import Events from './pages/events';
import FAQ from './pages/faq';
import Pricing from './pages/pricing';
import ProjectDetails from './pages/project-details';
import ServiceDetails from './pages/service-details';
import TeamDetails from './pages/team-details';
import DonationDetails from './pages/donation-details';
import DonationSetup from './pages/donation-setup';
import DonationIntent from './pages/donation-intent';
import DonationInterestForm from './pages/donation-interest-form';
import DonationPayment from './pages/donation-payment';
import DonationConfirmation from './pages/donation-confirmation';
import Donate from './pages/donate';
import NotFound from './pages/404';
import Index2 from './pages/index-2';
import Privacy from './pages/privacy';
import Terms from './pages/terms';
import PathioBuddies from './pages/pathio-buddies';

// Component to handle preloader and scripts
function AppContent() {
  const location = useLocation();

  useEffect(() => {
    // Prevent duplicate script loading
    if (window.scriptsLoaded) {
      return;
    }

    // Load external scripts
    const loadScript = (src) => {
      return new Promise((resolve, reject) => {
        // Check if script already exists
        const existingScript = document.querySelector(`script[src="${src}"]`);
        if (existingScript) {
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
      });
    };

    // Load scripts in order
    const scripts = [
      '/assets/vendor/bootstrap/bootstrap.bundle.min.js',
      '/assets/vendor/splide/splide.min.js',
      '/assets/vendor/splide/splide-extension-auto-scroll.min.js',
      '/assets/vendor/swiper/swiper-bundle.min.js',
      '/assets/vendor/slim-select/slimselect.min.js',
      '/assets/vendor/animate-wow/wow.min.js',
      '/assets/vendor/splittype/index.min.js',
      '/assets/vendor/mixitup/mixitup.min.js',
      '/assets/vendor/fslightbox/fslightbox.js',
      '/assets/vendor/flatpickr/flatpickr.js',
      '/assets/js/main.js',
      '/assets/js/tab.js',
      '/assets/js/accordion.js',
      '/assets/js/progressbar.js',
      '/assets/js/donate-form.js',
    ];

    // Load scripts sequentially
    scripts.reduce((promise, src) => {
      return promise.then(() => loadScript(src));
    }, Promise.resolve()).then(() => {
      window.scriptsLoaded = true;
      // Initialize WOW.js after scripts are loaded (only once)
      if (window.WOW && !window.__wowInitialized) {
        window.__wowInitialized = true;
        new window.WOW().init();
      }
    }).catch(err => {
      console.error('Error loading scripts:', err);
    });
  }, []);

  // Scroll to top on route change (unless there's a hash)
  useEffect(() => {
    // Don't scroll to top if there's a hash in the URL
    if (location.hash) {
      return;
    }
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, [location]);

  // Hide preloader on route change
  useEffect(() => {
    const hidePreloader = () => {
      const preloader = document.getElementById('preloader');
      if (preloader) {
        preloader.style.display = 'none';
        document.body.style.position = 'static';
      }
    };

    // Hide immediately
    hidePreloader();

    // Also hide in next frame to catch any timing issues
    requestAnimationFrame(() => {
      hidePreloader();
    });

    // Also hide on window load (for initial page load)
    if (document.readyState === 'complete') {
      hidePreloader();
    } else {
      window.addEventListener('load', hidePreloader);
    }

    return () => {
      window.removeEventListener('load', hidePreloader);
    };
  }, [location]);

  return null;
}

function App() {

  return (
    <Router>
      <AppContent />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/services" element={<Services />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/donations" element={<Donations />} />
        <Route path="/donate" element={<Donate />} />
        <Route path="/donations/setup" element={<DonationSetup />} />
        <Route path="/donations/intent" element={<DonationIntent />} />
        <Route path="/donations/interest-form" element={<DonationInterestForm />} />
        <Route path="/donations/payment" element={<DonationPayment />} />
        <Route path="/donations/confirmation" element={<DonationConfirmation />} />
        <Route path="/team" element={<Team />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog-2" element={<Blog2 />} />
        <Route path="/blog-details" element={<BlogDetails />} />
        <Route path="/event-details" element={<EventDetails />} />
        <Route path="/events" element={<Events />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/project-details" element={<ProjectDetails />} />
        <Route path="/service-details" element={<ServiceDetails />} />
        <Route path="/team-details" element={<TeamDetails />} />
        <Route path="/donation-details" element={<DonationDetails />} />
        <Route path="/index-2" element={<Index2 />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/pathio-buddies" element={<PathioBuddies />} />
        <Route path="/404" element={<NotFound />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
}

export default App;
